import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  output,
  input,
  effect,
  signal
} from '@angular/core'
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { SwiperContainer } from 'swiper/element'
import Swiper from 'swiper'
import { exists } from '@mediacoach/ui'
import { SwiperResizeDispatcherService } from '@shared/components/swiper-tabs/services/swiper-resize-dispatcher.service'
import { debounceTime, filter, tap } from 'rxjs/operators'
import { merge, fromEvent } from 'rxjs'

@Directive({
  selector: '[mcmSwiper]',
  host: {
    '[style.height.px]': 'hostHeight()'
  }
})
export class SwiperDirective implements AfterViewInit, OnDestroy {
  private readonly _el = inject<ElementRef<SwiperContainer>>(ElementRef)
  private readonly _zone = inject(NgZone)
  private readonly _resizeDispatcher = inject(SwiperResizeDispatcherService)

  private _swiperInstance: Swiper | null = null

  readonly hostHeight = signal<number | undefined>(undefined)

  readonly swiperActiveIndex = input<number, number | null | undefined>(0, {
    transform: (val) => (exists(val) ? (val as number) : 0)
  })

  readonly swiperUpdateTrigger = input<unknown>()
  readonly swiperContentLoading = input(false)

  readonly swiperSlideChange = output<number>()

  private get _host() {
    return this._el.nativeElement
  }

  constructor() {
    effect(() => {
      const index = this.swiperActiveIndex()
      if (this._swiperInstance && this._swiperInstance.activeIndex !== index) {
        this._swiperInstance.slideTo(index as number)
      }
    })

    merge(
      toObservable(this.swiperUpdateTrigger).pipe(tap(() => this._swiperInstance?.update())),
      toObservable(this.swiperContentLoading).pipe(filter((isLoading) => !isLoading && !!this._swiperInstance))
    )
      .pipe(
        debounceTime(150),
        tap(() => this._updateHeight()),
        takeUntilDestroyed()
      )
      .subscribe()

    merge(this._resizeDispatcher.dispatcher$, fromEvent(window, 'resize'))
      .pipe(
        debounceTime(100),
        tap(() => this._updateHeight()),
        takeUntilDestroyed()
      )
      .subscribe()
  }

  ngAfterViewInit(): void {
    this.initializeSwiper()
  }

  ngOnDestroy(): void {
    this._swiperInstance?.destroy()
  }

  private initializeSwiper(): void {
    if (this._swiperInstance) {
      return
    }

    const swiperParams = { initialSlide: this.swiperActiveIndex() }
    Object.assign(this._host, swiperParams)

    this._host.initialize()
    this._swiperInstance = this._host.swiper

    this._swiperInstance.on('slideChange', () => {
      this._zone.run(() => {
        this.swiperSlideChange.emit(this._swiperInstance!.activeIndex)
        setTimeout(() => this._updateHeight(), 150)
      })
    })
  }

  private _updateHeight() {
    const _ref = this._host.querySelector('.swiper-slide-active .mcm-swiper__slide-content')
    if (_ref) {
      this.hostHeight.set(_ref.getBoundingClientRect().height)
    }
  }
}
